import React, { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';
import { X } from 'lucide-react';
import ReactDOM from 'react-dom';
// Portal component for rendering dialog outside of parent hierarchy
const Portal = ({ children }) => {
    const [mounted, setMounted] = React.useState(false);
    const portalRef = React.useRef(null);

    React.useEffect(() => {
        portalRef.current = document.createElement('div');
        portalRef.current.setAttribute('id', 'dialog-portal');
        document.body.appendChild(portalRef.current);
        setMounted(true);

        return () => {
            if (portalRef.current) {
                document.body.removeChild(portalRef.current);
            }
        };
    }, []);

    return mounted && portalRef.current ? ReactDOM.createPortal(children, portalRef.current) : null;
};

export const Dialog = ({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    className,
    showClose = true,
    size = 'md', // 'sm' | 'md' | 'lg' | 'xl' | 'full'
}) => {
    // Handle ESC key press
    React.useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [open, onClose]);

    if (!open) return null;

    const sizeClasses = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'sm:max-w-2xl',
        full: 'sm:max-w-[calc(100vw-2rem)]'
    };

    return (
        <Portal>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                {/* Overlay */}
                <div
                    className="fixed inset-0 bg-black/50 transition-opacity"
                    onClick={onClose}
                />

                {/* Dialog positioning */}
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    {/* Dialog content */}
                    <div
                        className={twMerge(
                            'relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8',
                            sizeClasses[size],
                            className
                        )}
                    >
                        {/* Close button */}
                        {showClose && (
                            <button
                                onClick={onClose}
                                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </button>
                        )}

                        {/* Header */}
                        {(title || description) && (
                            <div className="px-6 py-4 sm:px-6">
                                {title && (
                                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                                        {title}
                                    </h3>
                                )}
                                {description && (
                                    <p className="mt-2 text-sm text-gray-500">
                                        {description}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Body */}
                        <div className="px-6 py-4 sm:px-6">
                            {children}
                        </div>

                        {/* Footer */}
                        {footer && (
                            <div className="flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end sm:space-x-2 sm:px-6">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

// Button components for the dialog
export const DialogButton = ({
    variant = 'default', // 'default' | 'primary' | 'danger'
    className,
    loading = false,
    ...props
}) => {
    const baseClasses = 'inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50  transition-colors';

    const variantClasses = {
        default: 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
        primary: 'bg-brand-primary text-white hover:bg-brand-primary/90 focus:ring-brand-primary',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    };

    return (
        <button
            className={twMerge(baseClasses, variantClasses[variant], className)}
            {...props}
        />
    );
};
